const IMAGEBED_HOST = 'pic.zhso.org'
const IMAGEBED_PROTOCOL = 'https:'
const IMG_SELECTOR = 'img[src], img[data-imagebed-src]'

// 透明 1x1 SVG：解密前作为合法 src 占位，视觉占位由 CSS 骨架承担。
export const PLACEHOLDER_SRC =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1' viewBox='0 0 1 1'%3E%3C/svg%3E"

const objectUrlRefs = new Map()
const pendingLoads = new Map()
const elementState = new WeakMap()

function hasWindow() {
  return typeof window !== 'undefined'
}

function safeParseUrl(input) {
  if (!input || !hasWindow()) return null
  try {
    return new URL(input, window.location.origin)
  } catch (_) {
    return null
  }
}

export function isImageBedUrl(input) {
  const parsed = safeParseUrl(input)
  return !!parsed && parsed.hostname === IMAGEBED_HOST
}

function normalizeImageBedUrl(input) {
  const parsed = safeParseUrl(input)
  if (!parsed || parsed.hostname !== IMAGEBED_HOST) return input
  parsed.protocol = IMAGEBED_PROTOCOL
  return parsed.toString()
}

function detectMimeType(url, fallback = '') {
  if (fallback) return fallback
  const parsed = safeParseUrl(url)
  const pathname = parsed?.pathname || ''
  const ext = pathname.split('.').pop()?.toLowerCase()
  if (ext === 'png') return 'image/png'
  if (ext === 'gif') return 'image/gif'
  if (ext === 'webp') return 'image/webp'
  if (ext === 'svg') return 'image/svg+xml'
  if (ext === 'jpeg' || ext === 'jpg') return 'image/jpeg'
  return 'image/jpeg'
}

function getSdk() {
  if (!hasWindow()) return null
  return window.ImageBed || null
}

async function loadViaSdk(url, options = {}) {
  const sdk = getSdk()
  if (!sdk?.loadImage) {
    throw new Error('ImageBed SDK 未加载')
  }
  const finalOptions = { type: detectMimeType(url, options.type), ...options }
  return sdk.loadImage(url, finalOptions)
}

export async function resolveImageUrl(url, options = {}) {
  if (!url || !isImageBedUrl(url)) return url

  const normalizedUrl = normalizeImageBedUrl(url)

  const cached = objectUrlRefs.get(normalizedUrl)
  if (cached) {
    cached.count += 1
    return cached.objectUrl
  }

  if (pendingLoads.has(normalizedUrl)) {
    const objectUrl = await pendingLoads.get(normalizedUrl)
    const entry = objectUrlRefs.get(normalizedUrl)
    if (entry) {
      entry.count += 1
      return entry.objectUrl
    }
    return objectUrl
  }

  const promise = loadViaSdk(normalizedUrl, options)
    .then((objectUrl) => {
      objectUrlRefs.set(normalizedUrl, { objectUrl, count: 0 })
      pendingLoads.delete(normalizedUrl)
      return objectUrl
    })
    .catch((error) => {
      pendingLoads.delete(normalizedUrl)
      throw error
    })

  pendingLoads.set(normalizedUrl, promise)

  const objectUrl = await promise
  const entry = objectUrlRefs.get(normalizedUrl)
  if (entry) {
    entry.count += 1
    return entry.objectUrl
  }
  return objectUrl
}

export function releaseImageUrl(url) {
  if (!url || !isImageBedUrl(url)) return

  const normalizedUrl = normalizeImageBedUrl(url)
  const entry = objectUrlRefs.get(normalizedUrl)
  if (!entry) return

  entry.count -= 1
  if (entry.count > 0) return

  const sdk = getSdk()
  if (sdk?.release) {
    sdk.release(entry.objectUrl)
  } else if (hasWindow()) {
    URL.revokeObjectURL(entry.objectUrl)
  }
  objectUrlRefs.delete(normalizedUrl)
}

function getDesiredSource(img) {
  return img.getAttribute('data-imagebed-src') || img.getAttribute('src') || ''
}

function releaseElement(img) {
  const state = elementState.get(img)
  if (!state) return
  releaseImageUrl(state.source)
  elementState.delete(img)
}

async function hydrateImageElement(img) {
  if (!img || img.tagName !== 'IMG') return

  // 兼容：若 src 直接是图床 URL 但没有 data-imagebed-src，先把来源备份到 data 属性，
  // 避免下面把 src 换成占位后丢失原始 URL。
  const dataSrc = img.getAttribute('data-imagebed-src') || ''
  const rawSrc = img.getAttribute('src') || ''
  if (!dataSrc && isImageBedUrl(rawSrc)) {
    img.setAttribute('data-imagebed-src', rawSrc)
  }

  const source = getDesiredSource(img)
  const normalizedSource = normalizeImageBedUrl(source)
  const current = elementState.get(img)

  if (!source || !isImageBedUrl(source)) {
    if (current) {
      releaseElement(img)
    }
    if (!img.getAttribute('src') && source) {
      img.setAttribute('src', source)
    }
    return
  }

  if (current?.source === normalizedSource) return

  if (current) {
    releaseElement(img)
  }

  // 解密前：立即把 src 换成透明占位并打 loading class，阻断浏览器加载加密字节，
  // 消除「先裂开图标再显示真图」。先写 elementState，借助上面的短路避免
  // 设置 src 触发 MutationObserver 后重复进入。
  elementState.set(img, { source: normalizedSource })
  img.classList.remove('imagebed-error')
  img.setAttribute('src', PLACEHOLDER_SRC)
  img.classList.add('imagebed-loading')

  try {
    const resolved = await resolveImageUrl(normalizedSource)
    if (normalizeImageBedUrl(getDesiredSource(img)) !== normalizedSource) {
      releaseImageUrl(normalizedSource)
      return
    }
    img.classList.remove('imagebed-error')
    img.classList.remove('imagebed-loading')
    if (img.getAttribute('src') !== resolved) {
      img.setAttribute('src', resolved)
    }
  } catch (error) {
    console.warn('ImageBed load failed:', error)
    // 不再回退到加密 URL（那会显示裂开图标），保持占位灰底
    img.classList.remove('imagebed-loading')
    img.classList.add('imagebed-error')
  }
}

function walkImages(node, visitor) {
  if (!node) return
  if (node.nodeType === 1 && node.tagName === 'IMG') {
    visitor(node)
  }
  if (node.nodeType === 1 && node.querySelectorAll) {
    node.querySelectorAll(IMG_SELECTOR).forEach(visitor)
  }
}

export function installImageBedObserver(root = document.body) {
  if (!hasWindow() || !root || !root.querySelectorAll) {
    return () => {}
  }

  walkImages(root, hydrateImageElement)

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.target?.tagName === 'IMG') {
        hydrateImageElement(mutation.target)
      }

      mutation.addedNodes.forEach((node) => {
        walkImages(node, hydrateImageElement)
      })

      mutation.removedNodes.forEach((node) => {
        walkImages(node, releaseElement)
      })
    })
  })

  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'data-imagebed-src'],
  })

  return () => {
    observer.disconnect()
    walkImages(root, releaseElement)
  }
}
